import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, TextField, Alert } from '@mui/material';

const EDITABLE_FIELDS = [
  { key: 'dbh', label: 'Avg DBH (cm)' },
  { key: 'avgHeight', label: 'Avg Height (m)' },
  { key: 'treesPerHectare', label: 'Trees/Ha' },
  { key: 'areaInHa', label: 'Area (Ha)' },
  { key: 'conversionFactor', label: 'Conversion Factor (CF)' },
  { key: 'percentCovered', label: 'Forest Covered (%)' },
  { key: 'brashPercent', label: 'Brash (%)' },
];

const CALCULATED_FIELDS = [
  { key: 'avgTreeSize', label: 'Avg Tree M3' },
  { key: 'brash', label: 'Brash M3' },
  { key: 'totalM3', label: 'Tree M3' },
  { key: 'totalVolume', label: 'Total M3' },
  { key: 'totalVolPerHa', label: 'M3/Ha' },
  { key: 'totalTons', label: 'Total Tons' },
  { key: 'totalTonsPerHa', label: 'Tons/Ha' },
];

const num = v => (v === '' || v === null || v === undefined ? 0 : Number(v));

const OverviewInfoTable = ({ subCompartmentId, subCompartmentData = {}, onUpdate }) => {
  const [values, setValues] = useState({
    dbh: 0,
    avgHeight: 0,
    treesPerHectare: 0,
    areaInHa: 0,
    conversionFactor: 1.15,
    percentCovered: 90,
    brashPercent: 0,
  });

  // Load existing data on mount ONLY
  useEffect(() => {
    setValues({
      dbh: subCompartmentData.updatedDbh || 0,
      avgHeight: subCompartmentData.updatedAvgHeight || 0,
      treesPerHectare: subCompartmentData.updatedTreesPerHectare || 0,
      areaInHa: subCompartmentData.updatedAreaInHa || subCompartmentData.area || 0,
      conversionFactor: subCompartmentData.updatedConversionFactor || 1.15,
      percentCovered: subCompartmentData.updatedPercentCovered || 90,
      brashPercent: subCompartmentData.updatedBrashPercent || 0,
    });
  }, []);

  // Calculate derived values
  const calculated = (() => {
    const avgTreeSize = (() => {
      const dbh = num(values.dbh);
      const avgHeight = num(values.avgHeight);
      if (!dbh || !avgHeight) return 0;
      const radius = dbh / 100 / 2;
      return Number((0.8 * Math.PI * radius ** 2 * avgHeight).toFixed(2));
    })();

    const grossM3 = avgTreeSize * num(values.treesPerHectare) * num(values.areaInHa);
    const netM3 = grossM3 * (num(values.percentCovered) / 100);

    const brash = Number((netM3 * (num(values.brashPercent) / 100)).toFixed(2));
    const totalM3 = Number(netM3.toFixed(2));
    const totalVolume = Number((netM3 + brash).toFixed(2));
    const totalVolPerHa = num(values.areaInHa) ? Number((totalVolume / num(values.areaInHa)).toFixed(2)) : 0;
    const totalTons = num(values.conversionFactor) ? Number((totalVolume / num(values.conversionFactor)).toFixed(2)) : 0;
    const totalTonsPerHa = num(values.areaInHa) ? Number((totalTons / num(values.areaInHa)).toFixed(2)) : 0;

    return {
      avgTreeSize,
      brash,
      totalM3,
      totalVolume,
      totalVolPerHa,
      totalTons,
      totalTonsPerHa,
    };
  })();

  // Auto-save on change
  useEffect(() => {
    const timeout = setTimeout(() => {
      onUpdate(subCompartmentId, {
        updatedDbh: num(values.dbh),
        updatedAvgHeight: num(values.avgHeight),
        updatedTreesPerHectare: num(values.treesPerHectare),
        updatedAreaInHa: num(values.areaInHa),
        updatedConversionFactor: num(values.conversionFactor),
        updatedPercentCovered: num(values.percentCovered),
        updatedBrashPercent: num(values.brashPercent),
        updatedAvgTreeSize: calculated.avgTreeSize,
        updatedBrash: calculated.brash,
        updatedTotalM3: calculated.totalM3,
        updatedTotalVolume: calculated.totalVolume,
        updatedTotalVolPerHa: calculated.totalVolPerHa,
        updatedTotalTons: calculated.totalTons,
        updatedTotalTonsPerHa: calculated.totalTonsPerHa,
      });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [values, calculated, subCompartmentId, onUpdate]);

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        All values are user-entered. Calculated fields update automatically.
      </Alert>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Field</strong>
            </TableCell>
            <TableCell>
              <strong>Value</strong>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {EDITABLE_FIELDS.map(field => (
            <TableRow key={field.key}>
              <TableCell>{field.label}</TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={values[field.key]}
                  onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
                  fullWidth
                />
              </TableCell>
            </TableRow>
          ))}

          {CALCULATED_FIELDS.map(field => (
            <TableRow key={field.key}>
              <TableCell>{field.label}</TableCell>
              <TableCell>{calculated[field.key]}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default OverviewInfoTable;
